/**
 * ProductListPage — Main product listing page with faceted filtering
 *
 * Route: /products/category/:subcategorySlug
 *
 * Layout:
 *   Desktop — 280px FilterPanel sidebar + main content
 *   Mobile  — filter panel hidden behind "Filters" button (slide-out drawer)
 *
 * Top bar: result count, "Show only" dropdown, ViewToggle (grid/list)
 * Product cards rendered inline (list + grid variants)
 * Pagination at bottom
 *
 * CTA logic per lifecycle:
 *   PICS=S (Standard) + sellable  -> "Add to cart"
 *   PICS=N (By Demand)            -> "By request only"
 *   PICS=U (Outphased)            -> "Show recommendation"
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { loadAllProducts, filterByPicsView } from '@services/dataService';
import { useAvailability } from '@hooks/useAvailability';
import { useAuth } from '@contexts/AuthContext';
import { useCart } from '@contexts/CartContext';
import useRegion from '@hooks/useRegion';
import { PRODUCT_LINES, FACET_CONFIG, CATEGORY_CONFIG, isCommerceEnabledForProductLine } from '@data/index';
import Breadcrumbs from '@components/layout/Breadcrumbs';
import FilterPanel from '@components/catalog/FilterPanel';
import RichContentPage from '@pages/RichContentPage';
import CutterSelectorMatrix from '@components/raiseboring/CutterSelectorMatrix';
import ViewToggle from '@components/catalog/ViewToggle';
import Pagination from '@components/catalog/Pagination';
import ShowOnlyFilter from '@components/product/ShowOnlyFilter';
import Toast from '@components/common/Toast';
import AddToFavouritesModal from '@components/favourites/AddToFavouritesModal';
import { useContactModal } from '@contexts/ContactModalContext';

// ---------------------------------------------------------------------------
// Helpers: resolve subcategory context from navigation tree
// ---------------------------------------------------------------------------

function resolveSubcategory(slug) {
  const domainMap = {
    'top-hammer': 'tophammer',
    'down-the-hole': 'dth',
    'rotary': 'rotary',
  };

  for (const [lineId, line] of Object.entries(PRODUCT_LINES)) {
    if (!line.categories) continue;
    for (const cat of line.categories) {
      if (!cat.subcategories) continue;
      for (const sub of cat.subcategories) {
        if (sub.id === slug) {
          return {
            productLineId: lineId,
            productLineTitle: line.shortTitle || line.title,
            productLineRoute: line.route,
            domain: domainMap[lineId],
            category: cat,
            subcategory: sub,
          };
        }
      }
    }
  }
  return null;
}

function resolveField(facet) {
  if (typeof facet.field === 'string') return facet.field;
  if (facet.field?.metric) return facet.field.metric;
  return facet.id;
}

// ---------------------------------------------------------------------------
// Image fallback handler — hides broken <img> and shows icon placeholder
// ---------------------------------------------------------------------------

function handleImageError(e) {
  const img = e.currentTarget;
  img.style.display = 'none';
  const fallback = img.parentElement?.querySelector('[data-img-fallback]');
  if (fallback) fallback.style.display = 'flex';
}

// ---------------------------------------------------------------------------
// Filter logic
// ---------------------------------------------------------------------------

function applyFilters(products, facets, filters) {
  if (!filters || Object.keys(filters).length === 0) return products;

  return products.filter((product) => {
    return facets.every((facet) => {
      const filterVal = filters[facet.id];
      if (filterVal == null) return true;

      const field = resolveField(facet);

      if (facet.type === 'checkbox' && Array.isArray(filterVal) && filterVal.length > 0) {
        const prodVal = product[field];
        if (prodVal == null) return false;
        const prodValues = Array.isArray(prodVal) ? prodVal.map(String) : [String(prodVal)];
        return filterVal.some((v) => prodValues.includes(v));
      }

      if (facet.type === 'range') {
        const prodVal = parseFloat(product[field]);
        if (isNaN(prodVal)) return false;
        if (filterVal.min != null && prodVal < filterVal.min) return false;
        if (filterVal.max != null && prodVal > filterVal.max) return false;
      }

      return true;
    });
  });
}

function sortProducts(products, sortBy) {
  if (!sortBy || sortBy === 'default') return products;

  const sorted = [...products];
  switch (sortBy) {
    case 'name-asc':
      sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      break;
    case 'name-desc':
      sorted.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
      break;
    case 'diameter-asc':
      sorted.sort((a, b) => {
        const da = parseFloat(a.diameterMm || a.bitHeadDiameter_mm || 0);
        const db = parseFloat(b.diameterMm || b.bitHeadDiameter_mm || 0);
        return da - db;
      });
      break;
    default:
      break;
  }
  return sorted;
}

// ---------------------------------------------------------------------------
// Availability badge
// ---------------------------------------------------------------------------

function ProductAvailabilityBadge({ pics }) {
  if (!pics) return null;
  const labels = { S: 'Standard', R: 'Regional', N: 'By Demand', U: 'Outphased' };
  const colors = {
    S: 'bg-green-100 text-green-800',
    R: 'bg-blue-100 text-blue-800',
    N: 'bg-amber-100 text-amber-800',
    U: 'bg-n1 text-n5',
  };
  const label = labels[pics] || pics;
  const color = colors[pics] || 'bg-n1 text-n5';

  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-sandvik-text ${color}`}>
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Lifecycle-aware CTA button
// Per brand-guidelines.md: buttons get `rounded` (4px), pills/badges `rounded-full`
// All active CTA buttons use sandvik-blue per legacy app pattern
// ---------------------------------------------------------------------------

function LifecycleCTA({ pics, sellable, isAuthenticated, product, onAddToCart, openContact, className }) {
  if (!isAuthenticated) return null;
  // Non-commerce product lines (rotary, raiseboring, services) suppress every
  // CTA in the lifecycle chain — Add-to-cart, "By request", "Show recommendation",
  // and the "not available in your region" disabled state. Discovery of these
  // products happens via Contact-us instead. Source of truth: commerceEnabled
  // flag on the product-line node in navigationContent.json.
  if (!isCommerceEnabledForProductLine(product?.productLine)) return null;

  const base = 'w-44 px-5 py-2.5 rounded text-sm font-sandvik-text font-normal transition-colors text-center';

  if (pics === 'U') {
    return (
      <button
        type="button"
        onClick={() => openContact('request-alternate', {
          sku: product.itemCode || product.id,
          productName: product.description || product.name || product.itemCode,
        })}
        className={`${base} bg-sandvik-blue text-white hover:bg-sandvik-blue/90 ${className || ''}`}
      >
        Show recommendation
      </button>
    );
  }

  if (pics === 'N') {
    return (
      <button
        type="button"
        onClick={() => openContact('request-availability', {
          sku: product.itemCode || product.id,
          productName: product.description || product.name || product.itemCode,
        })}
        className={`${base} bg-sandvik-blue text-white hover:bg-sandvik-blue/90 ${className || ''}`}
      >
        By request only
      </button>
    );
  }

  if (sellable && onAddToCart) {
    return (
      <button
        type="button"
        onClick={() => onAddToCart(product)}
        className={`${base} bg-sandvik-blue text-white hover:bg-sandvik-blue/90 ${className || ''}`}
      >
        Add to cart
      </button>
    );
  }

  // PICS=S or R but not sellable in user's region
  if ((pics === 'S' || pics === 'R') && !sellable) {
    return (
      <div className={`flex flex-col items-start gap-1 ${className || ''}`}>
        <button
          type="button"
          disabled
          className={`${base} bg-n3 text-n5 cursor-not-allowed`}
        >
          Add to cart
        </button>
        <span className="text-[11px] text-n5 font-sandvik-text leading-tight">
          Currently not available in your region.
          <br />Contact us for enquiry.
        </span>
      </div>
    );
  }

  return null;
}

// ---------------------------------------------------------------------------
// Product image component with fallback
// ---------------------------------------------------------------------------

function ProductImage({ src, alt, className, iconSize = 'text-4xl' }) {
  return (
    <div className={`bg-n1 flex items-center justify-center relative ${className || ''}`}>
      {src ? (
        <>
          <img
            src={src}
            alt={alt || ''}
            className="w-full h-full object-contain"
            onError={handleImageError}
            loading="lazy"
          />
          <div
            data-img-fallback
            className="absolute inset-0 items-center justify-center bg-n1"
            style={{ display: 'none' }}
          >
            <span className={`material-symbols-outlined ${iconSize} text-n3`}>build</span>
          </div>
        </>
      ) : (
        <span className={`material-symbols-outlined ${iconSize} text-n3`}>build</span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline product cards
// ---------------------------------------------------------------------------

function ProductListCard({ product, plpAttributes, subcategorySlug, onAddToCart, onFavourite }) {
  const productUrl = `/products/detail/${product.itemCode || product.id || ''}`;
  const { pics, sellable } = useAvailability(product.itemCode, product);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { openContact } = useContactModal();

  return (
    <div className="border border-n2 bg-white hover:border-n3 transition-colors group p-5">
      <div className="flex items-start gap-5">
        {/* Product image */}
        <Link to={productUrl} className="flex-shrink-0 w-32 h-24">
          <ProductImage
            src={product.imageUrl}
            alt={product.description}
            className="w-full h-full"
            iconSize="text-4xl"
          />
        </Link>

        {/* Main content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <Link
              to={productUrl}
              className="text-base font-sandvik-text font-normal text-carbon-black
                         group-hover:text-electric-blue transition-colors"
            >
              {product.description || product.itemCode || 'Unnamed product'}
            </Link>
            {pics === 'U' && (
              <span className="text-sm text-n5 font-sandvik-text">Outphased</span>
            )}
          </div>
          <p className="text-xs text-n5 font-sandvik-text mt-0.5">
            Part Number {product.itemCode || product.id}
          </p>

          {plpAttributes && plpAttributes.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-x-8 gap-y-1">
              {plpAttributes.map((attr) => {
                const val = product[attr.field];
                if (val == null || val === '') return null;
                return (
                  <div key={attr.field} className="text-sm font-sandvik-text">
                    <span className="text-n5">{attr.label}: </span>
                    <span className="text-carbon-black font-normal">
                      {String(val)}
                      {attr.suffix || ''}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          <Link
            to={productUrl}
            className="mt-2 inline-flex items-center gap-0.5 text-xs text-carbon-black hover:underline"
          >
            View more details
            <span className="material-symbols-outlined text-sm text-vibrant-orange">chevron_right</span>
          </Link>

          {pics && pics !== 'U' && (
            <div className="mt-2">
              <ProductAvailabilityBadge pics={pics} />
            </div>
          )}
        </div>

        {/* Right action column */}
        <div className="flex-shrink-0 flex flex-col items-end gap-2">
          <div className="flex items-center gap-1">
            <button type="button" title="Add to compare"
              className="w-8 h-8 inline-flex items-center justify-center text-n4 hover:text-electric-blue transition-colors">
              <span className="material-symbols-outlined text-lg">compare_arrows</span>
            </button>
            <button type="button" title="Add to favourites" onClick={() => onFavourite?.(product)}
              className="w-8 h-8 inline-flex items-center justify-center text-n4 hover:text-vibrant-orange transition-colors">
              <span className="material-symbols-outlined text-lg">bookmark_border</span>
            </button>
            <button type="button" title="Contact us about this product"
              className="w-8 h-8 inline-flex items-center justify-center text-n4 hover:text-electric-blue transition-colors">
              <span className="material-symbols-outlined text-lg">mail_outline</span>
            </button>
          </div>

          <LifecycleCTA
            pics={pics}
            sellable={sellable}
            isAuthenticated={isAuthenticated}
            product={product}
            onAddToCart={onAddToCart}
            openContact={openContact}
          />

          <label className="flex items-center gap-1.5 text-xs font-sandvik-text cursor-pointer">
            <input type="checkbox" className="w-4 h-4 accent-electric-blue" />
            Compare
          </label>

          <button className="text-xs text-carbon-black hover:underline font-sandvik-text flex items-center gap-0.5">
            Contact us
            <span className="material-symbols-outlined text-sm text-vibrant-orange">chevron_right</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function ProductGridCard({ product, plpAttributes, subcategorySlug, onAddToCart, onFavourite }) {
  const productUrl = `/products/detail/${product.itemCode || product.id || ''}`;
  const { pics, sellable } = useAvailability(product.itemCode, product);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { openContact } = useContactModal();

  return (
    <div className="flex flex-col border border-n2 bg-white hover:border-n3 transition-colors group">
      <Link to={productUrl} className="w-full aspect-square max-h-40">
        <ProductImage
          src={product.imageUrl}
          alt={product.description}
          className="w-full h-full"
          iconSize="text-5xl"
        />
      </Link>

      <div className="p-3 flex-1 flex flex-col">
        <Link
          to={productUrl}
          className="text-sm font-sandvik-text font-normal text-carbon-black
                     group-hover:text-electric-blue transition-colors line-clamp-2"
        >
          {product.description || product.itemCode || 'Unnamed product'}
        </Link>
        <p className="text-xs text-n5 font-sandvik-text mt-0.5">
          {product.itemCode || product.id}
        </p>

        {plpAttributes && plpAttributes.length > 0 && (
          <div className="mt-2 space-y-0.5">
            {plpAttributes.slice(0, 3).map((attr) => {
              const val = product[attr.field];
              if (val == null || val === '') return null;
              return (
                <div key={attr.field} className="text-xs font-sandvik-text">
                  <span className="text-n5">{attr.label}: </span>
                  <span className="text-carbon-black font-normal">
                    {String(val)}{attr.suffix || ''}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-auto pt-3 flex items-center justify-between">
          {pics ? <ProductAvailabilityBadge pics={pics} /> : <span />}
          <div className="flex items-center gap-0.5">
            <button type="button" title="Add to compare"
              className="w-7 h-7 inline-flex items-center justify-center text-n4 hover:text-electric-blue transition-colors">
              <span className="material-symbols-outlined text-base">compare_arrows</span>
            </button>
            <button type="button" title="Add to favourites" onClick={() => onFavourite?.(product)}
              className="w-7 h-7 inline-flex items-center justify-center text-n4 hover:text-vibrant-orange transition-colors">
              <span className="material-symbols-outlined text-base">bookmark_border</span>
            </button>
          </div>
        </div>

        <LifecycleCTA
          pics={pics}
          sellable={sellable}
          isAuthenticated={isAuthenticated}
          product={product}
          onAddToCart={onAddToCart}
          openContact={openContact}
          className="mt-2 w-full py-2"
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

export default function ProductListPage() {
  const { subcategorySlug } = useParams();
  const { dispatch: cartDispatch } = useCart();
  const [toast, setToast] = useState(null);
  const [favProduct, setFavProduct] = useState(null);

  const context = useMemo(() => resolveSubcategory(subcategorySlug), [subcategorySlug]);
  const { isAuthenticated } = useAuth();
  const { salesEntity } = useRegion();

  // D7 — sub-app slugs with renderAs:'rich' (e.g. Centrevo Track / Drill /
  // Analyze / Report) bypass the PLP grid and render the rich content page.
  if (context?.subcategory?.renderAs === 'rich') {
    return (
      <RichContentPage
        productLineData={PRODUCT_LINES[context.productLineId]}
        node={context.subcategory}
      />
    );
  }

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({});
  const [sortBy, setSortBy] = useState('default');
  const [viewMode, setViewMode] = useState('list');
  const [currentPage, setCurrentPage] = useState(1);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [showOnlyView, setShowOnlyView] = useState('all');

  const pageSize = 20;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setCurrentPage(1);

    loadAllProducts()
      .then((allProducts) => {
        if (!cancelled) {
          const filtered = (allProducts || []).filter(
            (p) => p.subcategory === subcategorySlug
          );
          setProducts(filtered);
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error('[ProductListPage] Load failed:', err);
        if (!cancelled) {
          setProducts([]);
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [subcategorySlug]);

  const facets = useMemo(() => {
    if (!context) return [];
    const catConf = CATEGORY_CONFIG?.[context.subcategory?.id];
    if (catConf?.facets) return catConf.facets;
    // Per-subcategory facets stored on the top level of facetConfig.json —
    // rotary uses this shape (11 subcategory keys with `.facets` arrays).
    const subFacets = FACET_CONFIG?.[context.subcategory?.id]?.facets;
    if (subFacets) return subFacets;
    // Legacy per-domain fallback (TH / DTH).
    const domainFacets = FACET_CONFIG?.[context.domain];
    return domainFacets || [];
  }, [context]);

  const plpAttributes = useMemo(() => {
    if (!context) return [];
    const catConf = CATEGORY_CONFIG?.[context.subcategory?.id];
    return catConf?.plpAttributes || [];
  }, [context]);

  const lifecycleProducts = useMemo(() => {
    if (showOnlyView === 'all') return products;
    return filterByPicsView(products, showOnlyView, salesEntity);
  }, [products, showOnlyView, salesEntity]);

  const filteredProducts = useMemo(
    () => applyFilters(lifecycleProducts, facets, filters),
    [lifecycleProducts, facets, filters]
  );
  const sortedProducts = useMemo(
    () => sortProducts(filteredProducts, sortBy),
    [filteredProducts, sortBy]
  );

  const totalItems = sortedProducts.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const pagedProducts = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedProducts.slice(start, start + pageSize);
  }, [sortedProducts, currentPage, pageSize]);

  const handleFilterChange = useCallback((facetId, value) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (value == null || (Array.isArray(value) && value.length === 0)) {
        delete next[facetId];
      } else {
        next[facetId] = value;
      }
      return next;
    });
    setCurrentPage(1);
  }, []);

  const handleClearAll = useCallback(() => {
    setFilters({});
    setCurrentPage(1);
  }, []);

  const handleAddToCart = useCallback((product) => {
    cartDispatch({
      type: 'ADD_ITEM',
      payload: {
        itemCode: product.itemCode,
        description: product.description,
        qty: 1,
        unitPrice: product.price || 0,
        currency: product.currency || 'CAD',
        image: product.imageUrl || '/images/placeholders/test-product-1.png',
      },
    });
    setToast({ message: `${product.description} added to cart`, type: 'success' });
    setTimeout(() => setToast(null), 3000);
  }, [cartDispatch]);

  const breadcrumbItems = useMemo(() => {
    if (!context) return [{ label: subcategorySlug || 'Products', to: null, isLast: true }];
    return [
      { label: context.productLineTitle, to: context.productLineRoute },
      { label: context.category?.title || '', to: `${context.productLineRoute}/${context.category?.id || ''}` },
      { label: context.subcategory?.title || subcategorySlug, to: null, isLast: true },
    ];
  }, [context, subcategorySlug]);

  const pageTitle = context?.subcategory?.title || subcategorySlug?.replace(/-/g, ' ')?.replace(/^\w/, (c) => c.toUpperCase()) || 'Products';

  return (
    <div className="sandvik-container py-6">
      <Breadcrumbs items={breadcrumbItems} />

      {/* Raiseboring cutter selection matrix — shown above the product grid
          on the cutters PLP only. Uses the marcom-approved cutter selection
          reference chart to help customers pick the right cutter for their
          rock condition (Formation label / UCS / CAI). */}
      {subcategorySlug === 'raiseboring-cutters' && <CutterSelectorMatrix />}

      <h1 className="text-2xl font-sandvik-headline font-normal text-carbon-black mb-4">
        {pageTitle}
      </h1>

      <div className="flex gap-6">
        <div className="hidden lg:block w-[280px] flex-shrink-0">
          <FilterPanel
            facets={facets}
            products={lifecycleProducts}
            activeFilters={filters}
            onFilterChange={handleFilterChange}
            onClearAll={handleClearAll}
          />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-n2">
            <div className="flex items-center gap-3">
              <span className="text-sm text-n5 font-sandvik-text">
                {totalItems} {totalItems === 1 ? 'product' : 'products'}
              </span>

              <button
                type="button"
                onClick={() => setMobileFiltersOpen(true)}
                className="lg:hidden flex items-center gap-1 px-3 py-1.5 border border-n2 rounded-none
                           text-sm font-sandvik-text text-carbon-black hover:bg-n1 transition-colors"
              >
                <span className="material-symbols-outlined text-base">filter_list</span>
                Filters
              </button>

              {Object.keys(filters).length > 0 && (
                <span className="inline-flex items-center gap-1 text-xs text-electric-blue font-sandvik-text">
                  <span className="w-5 h-5 rounded-full bg-electric-blue text-white flex items-center justify-center text-xs">
                    {Object.keys(filters).length}
                  </span>
                  active
                </span>
              )}
            </div>

            <div className="flex items-center gap-3">
              <ShowOnlyFilter
                value={showOnlyView}
                onChange={setShowOnlyView}
              />
              <div className="flex items-center gap-2">
                <label htmlFor="sort-select" className="text-xs text-n5 font-sandvik-text hidden sm:block">
                  Sort by
                </label>
                <select
                  id="sort-select"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-2.5 py-1.5 border border-n2 rounded-none text-sm
                             font-sandvik-text text-carbon-black bg-white
                             focus:outline-none focus:border-electric-blue"
                >
                  <option value="default">Default</option>
                  <option value="name-asc">Name A-Z</option>
                  <option value="name-desc">Name Z-A</option>
                  <option value="diameter-asc">Diameter (small-large)</option>
                </select>
              </div>

              <ViewToggle viewMode={viewMode} onChange={setViewMode} />
            </div>
          </div>

          {loading && (
            <div className="flex items-center justify-center py-20">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-2 border-n2 border-t-electric-blue rounded-full animate-spin" />
                <p className="text-sm text-n5 font-sandvik-text">Loading products...</p>
              </div>
            </div>
          )}

          {!loading && totalItems === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <span className="material-symbols-outlined text-5xl text-n3 mb-3">
                search_off
              </span>
              <h2 className="text-lg font-sandvik-headline font-normal text-carbon-black mb-1">
                No products found
              </h2>
              <p className="text-sm text-n5 font-sandvik-text mb-4">
                {Object.keys(filters).length > 0
                  ? 'Try adjusting your filters to see more results.'
                  : 'No products are available in this category.'}
              </p>
              {Object.keys(filters).length > 0 && (
                <button
                  type="button"
                  onClick={handleClearAll}
                  className="px-4 py-2 bg-sandvik-blue text-white text-sm font-sandvik-text
                             rounded hover:bg-sandvik-blue/90 transition-colors"
                >
                  Clear all filters
                </button>
              )}
            </div>
          )}

          {!loading && totalItems > 0 && (
            <>
              {viewMode === 'list' ? (
                <div className="space-y-2">
                  {pagedProducts.map((product, idx) => (
                    <ProductListCard
                      key={product.itemCode || product.id || idx}
                      product={product}
                      plpAttributes={plpAttributes}
                      subcategorySlug={subcategorySlug}
                      onAddToCart={handleAddToCart}
                      onFavourite={setFavProduct}
                    />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {pagedProducts.map((product, idx) => (
                    <ProductGridCard
                      key={product.itemCode || product.id || idx}
                      product={product}
                      plpAttributes={plpAttributes}
                      subcategorySlug={subcategorySlug}
                      onAddToCart={handleAddToCart}
                      onFavourite={setFavProduct}
                    />
                  ))}
                </div>
              )}

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
              />
            </>
          )}
        </div>
      </div>

      {mobileFiltersOpen && (
        <FilterPanel
          facets={facets}
          products={lifecycleProducts}
          activeFilters={filters}
          onFilterChange={handleFilterChange}
          onClearAll={handleClearAll}
          isMobile
          onClose={() => setMobileFiltersOpen(false)}
        />
      )}

      <AddToFavouritesModal
        product={favProduct}
        isOpen={!!favProduct}
        onClose={() => setFavProduct(null)}
        onSuccess={(msg) => {
          setToast({ type: 'success', message: msg });
          setFavProduct(null);
        }}
      />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type || 'success'}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
